import { useSessionManagementServiceV1 } from "@rw1/api-services";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  toApiMetricFilter,
  toCountryCertificationsEntry,
  toCountryHoursEntry,
  toCountrySessionEntry,
} from "../../../../domain/mappers/metricsMappers";
import type {
  CountryCertificationsEntry,
  CountryHoursEntry,
  CountrySessionEntry,
  Dimension,
} from "../../../../domain/models/metrics";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../../hooks/constants";
import { normalizeMetricDateRange } from "../../utils/metricDateRange";

export const COUNTRY_DATA_METRICS_QUERY_KEY = "countryDataMetrics";

const EMPTY_COUNTRY_DATA: {
  sessionEntries: CountrySessionEntry[];
  hoursEntries: CountryHoursEntry[];
  certificationEntries: CountryCertificationsEntry[];
} = { sessionEntries: [], hoursEntries: [], certificationEntries: [] };

interface UseCountryDataMetricsParams {
  startDate: Date;
  endDate: Date;
  dimension: Dimension;
  contentMetadataIds?: string[];
  countries?: string[];
  minSessionDurationInMinutes?: number;
  enabled?: boolean;
}

interface UseCountryDataMetricsResult {
  sessionEntries: CountrySessionEntry[];
  hoursEntries: CountryHoursEntry[];
  certificationEntries: CountryCertificationsEntry[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

export const useCountryDataMetrics = ({
  startDate,
  endDate,
  dimension,
  contentMetadataIds,
  countries,
  minSessionDurationInMinutes,
  enabled = true,
}: UseCountryDataMetricsParams): UseCountryDataMetricsResult => {
  const { companyId } = useParams();
  const sessionManagementService = useSessionManagementServiceV1();

  const normalizedRange = useMemo(
    () => normalizeMetricDateRange(startDate, endDate),
    [startDate, endDate],
  );

  const { startDate: normalizedStartDate, endDate: normalizedEndDate } =
    normalizedRange;

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: [
      COUNTRY_DATA_METRICS_QUERY_KEY,
      companyId,
      normalizedStartDate.toISOString(),
      normalizedEndDate.toISOString(),
      dimension,
      contentMetadataIds,
      countries,
      minSessionDurationInMinutes,
    ],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const filter = toApiMetricFilter({
        startDate: normalizedStartDate,
        endDate: normalizedEndDate,
        dimension,
        contentMetadataIds,
        countries,
        minSessionDurationInMinutes,
      });

      const result = await sessionManagementService.getCountryDataMetrics(
        companyId,
        filter,
      );

      const dataPoints = Array.isArray(result.countryDataPoints)
        ? result.countryDataPoints
        : [];

      return {
        sessionEntries: dataPoints.map(toCountrySessionEntry),
        hoursEntries: dataPoints.map(toCountryHoursEntry),
        certificationEntries: dataPoints.map(toCountryCertificationsEntry),
      };
    },
    enabled: Boolean(companyId) && enabled,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    placeholderData: keepPreviousData,
    retry: 1,
  });

  return {
    ...(data ?? EMPTY_COUNTRY_DATA),
    isLoading,
    isFetching,
    error: error ?? null,
  };
};
